/**
 * Theme Context for multi-tenant styling
 *
 * - Fetches user's assigned client/theme from backend
 * - Admins can override theme (persisted in localStorage)
 * - Applies CSS variables to :root when theme changes
 * - Updates favicon dynamically
 * - Super admin (boris@nextnovate.com) can "Act As" any user
 */

import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useUser } from './UserContext';
import { Theme, themes, getTheme, getThemeIds, DEFAULT_THEME, ADMIN_DEFAULT_THEME } from '../themes';

const ADMIN_THEME_OVERRIDE_KEY = 'admin_theme_override';
const ACT_AS_USER_KEY = 'super_admin_act_as';
const ADMIN_DOMAIN = 'nextnovate.com';
const SUPER_ADMIN_EMAIL = 'boris@nextnovate.com';

interface ActAsUser {
  email: string;
  name: string;
}

interface ThemeContextType {
  theme: Theme;
  themeId: string;
  assignedThemeId: string;  // User's actual assigned theme
  isAdmin: boolean;
  isSuperAdmin: boolean;    // Can impersonate any user
  isOverriding: boolean;    // Admin is previewing a different theme
  availableThemes: string[];
  availableUsers: ActAsUser[];  // Users super admin can act as
  actAsUser: ActAsUser | null;  // Currently impersonated user
  setThemeOverride: (themeId: string | null) => void;  // null = reset to assigned
  setActAsUser: (user: ActAsUser | null) => void;  // null = stop impersonating
  loading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Check if email is from admin domain
 */
function isAdminEmail(email: string | null): boolean {
  if (!email) return false;
  return email.toLowerCase().endsWith(`@${ADMIN_DOMAIN}`);
}

/**
 * Check if email is super admin (can impersonate any user)
 */
function isSuperAdminEmail(email: string | null): boolean {
  if (!email) return false;
  return email.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase();
}

/**
 * Apply theme CSS variables to :root
 */
function applyThemeToDocument(theme: Theme) {
  const root = document.documentElement;

  // Apply all color variables
  Object.entries(theme.colors).forEach(([key, value]) => {
    // Convert camelCase to kebab-case (e.g., primaryForeground -> primary-foreground)
    const cssVar = key.replace(/([A-Z])/g, '-$1').toLowerCase();
    root.style.setProperty(`--${cssVar}`, value);
  });

  // Update CSS custom properties for glow effects
  root.style.setProperty('--glow-color', theme.glowColor);
  // Create strong version by replacing opacity
  const strongGlow = theme.glowColor.replace(/[\d.]+\)$/, '0.5)');
  root.style.setProperty('--glow-color-strong', strongGlow);

  // Header background (optional, defaults to semi-transparent)
  if (theme.headerBackground) {
    root.style.setProperty('--header-background', theme.headerBackground);
  } else {
    root.style.removeProperty('--header-background');
  }
}

/**
 * Update favicon
 */
function updateFavicon(faviconUrl: string) {
  // Remove existing favicon links
  document.querySelectorAll("link[rel~='icon']").forEach(el => el.remove());

  // Create new favicon link
  const link = document.createElement('link');
  link.rel = 'icon';
  link.type = faviconUrl.endsWith('.svg') || faviconUrl.includes('svg') ? 'image/svg+xml' : 'image/x-icon';
  link.href = faviconUrl;
  document.head.appendChild(link);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user, getAuthHeaders } = useUser();
  const [assignedThemeId, setAssignedThemeId] = useState<string>(DEFAULT_THEME);
  const [overrideThemeId, setOverrideThemeId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [availableUsers, setAvailableUsers] = useState<ActAsUser[]>([]);
  const [actAsUser, setActAsUserState] = useState<ActAsUser | null>(null);

  const isAdmin = isAdminEmail(user?.email ?? null);
  const isSuperAdmin = isSuperAdminEmail(user?.email ?? null);

  // Determine effective theme ID
  const effectiveThemeId = overrideThemeId || assignedThemeId;
  const theme = getTheme(effectiveThemeId);
  const isOverriding = overrideThemeId !== null && overrideThemeId !== assignedThemeId;

  // Load admin override from localStorage on mount
  // Check localStorage even when not logged in (to persist theme on login page)
  useEffect(() => {
    const stored = localStorage.getItem(ADMIN_THEME_OVERRIDE_KEY);
    if (stored && themes[stored]) {
      // Use stored override if it exists (admin was previously logged in)
      setOverrideThemeId(stored);
    } else if (isAdmin) {
      // Default admins to nextnovate theme if no override set
      setOverrideThemeId(ADMIN_DEFAULT_THEME);
    } else {
      // Non-admins without stored override don't get override
      setOverrideThemeId(null);
    }
  }, [isAdmin]);

  // Load actAsUser from localStorage for super admin
  useEffect(() => {
    if (!isSuperAdmin) {
      setActAsUserState(null);
      return;
    }
    const stored = localStorage.getItem(ACT_AS_USER_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.email && parsed.name) {
          setActAsUserState(parsed);
        }
      } catch {
        localStorage.removeItem(ACT_AS_USER_KEY);
      }
    }
  }, [isSuperAdmin]);

  // Fetch available users for super admin "Act As" feature
  useEffect(() => {
    const fetchUsers = async () => {
      if (!isSuperAdmin || !user) {
        setAvailableUsers([]);
        return;
      }

      try {
        const headers = await getAuthHeaders();
        const response = await fetch('/api/users', { headers });
        if (response.ok) {
          const data = await response.json();
          const users = (data.users || [])
            .filter((u: { email: string }) => u.email !== user.email) // Exclude self
            .map((u: { email: string; name?: string; display_name?: string }) => ({
              email: u.email,
              name: u.name || u.display_name || u.email.split('@')[0],
            }));
          setAvailableUsers(users);
        }
      } catch (error) {
        console.warn('[Theme] Failed to fetch users for Act As:', error);
      }
    };

    fetchUsers();
  }, [isSuperAdmin, user, getAuthHeaders]);

  // Fetch user's assigned theme from backend
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) {
        setAssignedThemeId(DEFAULT_THEME);
        setLoading(false);
        return;
      }

      try {
        const headers = await getAuthHeaders();
        const response = await fetch('/api/user/profile', { headers });

        if (response.ok) {
          const data = await response.json();
          if (data.client && themes[data.client]) {
            setAssignedThemeId(data.client);
          } else {
            setAssignedThemeId(DEFAULT_THEME);
          }
        } else {
          // Endpoint might not exist yet, use default
          setAssignedThemeId(DEFAULT_THEME);
        }
      } catch (error) {
        console.warn('[Theme] Failed to fetch user profile:', error);
        setAssignedThemeId(DEFAULT_THEME);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [user, getAuthHeaders]);

  // Apply theme whenever it changes
  useEffect(() => {
    applyThemeToDocument(theme);
    updateFavicon(theme.favicon);

    // Update document title with brand
    document.title = theme.brandText;
  }, [theme]);

  // Admin theme override setter
  const setThemeOverride = useCallback((themeId: string | null) => {
    if (!isAdmin) return;

    if (themeId === null) {
      // Reset to assigned theme
      localStorage.removeItem(ADMIN_THEME_OVERRIDE_KEY);
      setOverrideThemeId(null);
    } else if (themes[themeId]) {
      // Set override
      localStorage.setItem(ADMIN_THEME_OVERRIDE_KEY, themeId);
      setOverrideThemeId(themeId);
    }
  }, [isAdmin]);

  // Super admin "Act As" setter
  const setActAsUser = useCallback((user: ActAsUser | null) => {
    if (!isSuperAdmin) return;

    if (user === null) {
      localStorage.removeItem(ACT_AS_USER_KEY);
      setActAsUserState(null);
    } else {
      localStorage.setItem(ACT_AS_USER_KEY, JSON.stringify(user));
      setActAsUserState(user);
    }
  }, [isSuperAdmin]);

  const value: ThemeContextType = {
    theme,
    themeId: effectiveThemeId,
    assignedThemeId,
    isAdmin,
    isSuperAdmin,
    isOverriding,
    availableThemes: getThemeIds(),
    availableUsers,
    actAsUser,
    setThemeOverride,
    setActAsUser,
    loading,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
